import styled from '@emotion/styled';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import useResourceLoader from '../../util/useResourceLoader';
import Notification from '../Notification';
import Loader from '../Loader';
import { useCallback, useEffect, useState, useRef } from 'react';
import ReportsApi from '../../api/ReportsApi';
import ProgressiveBackoff from '../../util/ProgressiveBackoff';

function Dashboard({
  guestToken,
  loadingGuestToken,
  errorLoadingGuestToken,
  dashboardUuid,
  height,
  hideTitle,
  hideTab,
  hideChartControls,
  filters,
  urlParams,
}) {
  return (
    <>
      {loadingGuestToken ? (
        <Loader fullscreen />
      ) : guestToken ? (
        <SupersetDashboard
          dashboardUuid={dashboardUuid}
          token={guestToken?.token}
          url={guestToken?.url}
          height={height}
          hideTitle={hideTitle}
          hideTab={hideTab}
          hideChartControls={hideChartControls}
          filters={filters}
          urlParams={urlParams}
        />
      ) : errorLoadingGuestToken ? (
        <Notification error={errorLoadingGuestToken} />
      ) : (
        <Notification error="Could not get dashboard from backend." />
      )}
    </>
  );
}

export function SupersetDashboard({ id, dashboardUuid, token, url, height, hideTitle, hideTab, hideChartControls, filters, urlParams }) {
  const ref = useRef();
  const [dashboard, setDashboard] = useState();
  const [innerHeight, setInnerHeight] = useState(height);
  const fitToHeight = !height;
  const refCallback = useCallback(
    async refElement => {
      if (!refElement) return;
      ref.current = refElement;

      // Use Idenfier (id) if available (from backend resolution), otherwise fallback to UUID
      const dashboardId = id || dashboardUuid;

      const dashboard = await embedDashboard({
        id: dashboardId,
        supersetDomain: url,
        mountPoint: refElement,
        fetchGuestToken: () => token,
        dashboardUiConfig: {
          // dashboard UI config: hideTitle, hideTab, hideChartControls, filters.visible, filters.expanded (optional), urlParams (optional)
          hideTitle,
          hideTab,
          hideChartControls,
          filters,
          urlParams: urlParams,
        },
        iframeSandboxExtras: ['allow-top-navigation', 'allow-popups-to-escape-sandbox'],
        referrerPolicy: 'origin',
      });

      setDashboard(dashboard);
    },
    [id, dashboardUuid, token, hideTitle, hideTab, hideChartControls, filters, urlParams]
  );

  useEffect(() => {
    if (fitToHeight && dashboard && ref.current) {
      const backoffTimeout = new ProgressiveBackoff({ initialDelay: 500, maxDelay: 5000, factor: 2 });

      async function checkHeight() {
        const { height } = await dashboard.getScrollSize();
        const iframe = ref.current.querySelector('iframe');

        if (iframe && (!innerHeight || innerHeight < height)) {
          setInnerHeight(height);
        }

        backoffTimeout.wait().then(checkHeight);
      }

      checkHeight();

      return () => {
        backoffTimeout.reset();
      };
    }
  }, [dashboard, fitToHeight, ref, innerHeight]);

  // return <div>Dashboard</div>
  return (
    <DashboardContainer ref={refCallback} height={innerHeight}>
      Dashboard
    </DashboardContainer>
  );
}

const DashboardContainer = styled.div`
  iframe {
    width: 100%;
    height: ${({ height }) => (height ? (typeof height === 'number' ? `${height}px` : height) : '100%')};
    border: 0;
  }
`;

export default Dashboard;
